import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './CompanyHistory.css';

const CertificationManagement = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  
  const [formData, setFormData] = useState({
    category: '',
    name: '',
    image: '',
    pdf: '',
    imageUrl: '',
    pdfUrl: '',
    order: 0
  });

  // Firebase에서 데이터 불러오기
  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const certificatesCollection = collection(db, 'certificates');
      const certificatesSnapshot = await getDocs(certificatesCollection);
      
      const certificatesData = certificatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // order 기준으로 정렬
      certificatesData.sort((a, b) => a.order - b.order);
      
      setCertificates(certificatesData);
    } catch (error) {
      console.error('인증서 데이터를 불러오는 중 오류 발생:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCertificates();
  }, []);

  // 폼 데이터 초기화
  const resetForm = () => {
    setFormData({
      category: '',
      name: '',
      image: '',
      pdf: '',
      imageUrl: '',
      pdfUrl: '',
      order: 0
    });
    setEditingCert(null);
    setShowAddForm(false);
  };

  const uploadFile = async ({ file, folder }) => {
    const safeName = (file?.name || 'file').replace(/[^\w.\-() ]/g, '_');
    const path = `certificates/${folder}/${Date.now()}_${safeName}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file, { contentType: file.type || undefined });
    const url = await getDownloadURL(storageRef);
    return { url, path };
  };

  // 인증서 추가
  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const certificatesCollection = collection(db, 'certificates');
      await addDoc(certificatesCollection, formData);
      console.log('인증서가 추가되었습니다.');
      resetForm();
      fetchCertificates();
    } catch (error) {
      console.error('인증서 추가 중 오류 발생:', error);
    }
  };

  // 인증서 수정
  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const certRef = doc(db, 'certificates', editingCert.id);
      await updateDoc(certRef, formData);
      console.log('인증서가 수정되었습니다.');
      resetForm();
      fetchCertificates();
    } catch (error) {
      console.error('인증서 수정 중 오류 발생:', error);
    }
  };

  // 인증서 삭제
  const handleDelete = async (id) => {
    if (window.confirm('정말로 이 인증서를 삭제하시겠습니까?')) {
      try {
        await deleteDoc(doc(db, 'certificates', id));
        console.log('인증서가 삭제되었습니다.');
        fetchCertificates();
      } catch (error) {
        console.error('인증서 삭제 중 오류 발생:', error);
      }
    }
  };

  // 수정 모드로 전환
  const handleEditClick = (certificate) => {
    setEditingCert(certificate);
    setFormData({
      category: certificate.category,
      name: certificate.name,
      image: certificate.image,
      pdf: certificate.pdf,
      imageUrl: certificate.imageUrl || '',
      pdfUrl: certificate.pdfUrl || '',
      order: certificate.order
    });
    setShowAddForm(true);
  };

  if (loading) {
    return (
      <div className="company-history-page">
        <div className="history-container">
          <h1 className="page-category-title">인증서 관리</h1>
          <div className="section-container">
            <p>데이터를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="company-history-page">
      <div className="history-container">
        <h1 className="page-category-title">인증서 관리</h1>
        
        {/* 추가/수정 폼 */}
        {showAddForm && (
          <div className="section-container">
            <h2>{editingCert ? '인증서 수정' : '인증서 추가'}</h2>
            <form onSubmit={editingCert ? handleEdit : handleAdd} className="certificate-form">
              <div className="form-group">
                <label>카테고리:</label>
                <select 
                  value={formData.category} 
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  required
                >
                  <option value="">카테고리 선택</option>
                  <option value="ISO 인증서 (한글)">ISO 인증서 (한글)</option>
                  <option value="ISO 인증서 (영문)">ISO 인증서 (영문)</option>
                  <option value="ASME 인증서">ASME 인증서</option>
                  <option value="KC 인증서">KC 인증서</option>
                  <option value="특허권">특허권</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>인증서명:</label>
                <input 
                  type="text" 
                  value={formData.name} 
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>이미지 (업로드 권장)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const { url } = await uploadFile({ file, folder: 'images' });
                      setFormData((prev) => ({
                        ...prev,
                        image: file.name,
                        imageUrl: url,
                      }));
                    } catch (error) {
                      console.error('이미지 업로드 실패:', error);
                      alert('이미지 업로드에 실패했습니다.');
                    }
                  }}
                />
                <div style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                  현재 URL: {formData.imageUrl ? formData.imageUrl : '(없음)'}
                </div>
              </div>
              
              <div className="form-group">
                <label>PDF (업로드 권장)</label>
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const { url } = await uploadFile({ file, folder: 'pdfs' });
                      setFormData((prev) => ({
                        ...prev,
                        pdf: file.name,
                        pdfUrl: url,
                      }));
                    } catch (error) {
                      console.error('PDF 업로드 실패:', error);
                      alert('PDF 업로드에 실패했습니다.');
                    }
                  }}
                />
                <div style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                  현재 URL: {formData.pdfUrl ? formData.pdfUrl : '(없음)'}
                </div>
              </div>
              
              <div className="form-group">
                <label>순서:</label>
                <input 
                  type="number" 
                  value={formData.order} 
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})}
                  required
                />
              </div>
              
              <div className="form-actions">
                <button type="submit">{editingCert ? '수정' : '추가'}</button>
                <button type="button" onClick={resetForm}>취소</button>
              </div>
            </form>
          </div>
        )}

        {/* 인증서 목록 */}
        <div className="section-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
            <h2>인증서 목록</h2>
            <button onClick={() => setShowAddForm(true)} className="add-button">
              새 인증서 추가
            </button>
          </div>
          
          <div className="certificate-management-list">
            {certificates.map((cert) => (
              <div key={cert.id} className="certificate-management-item">
                <div className="certificate-preview">
                  <img
                    src={cert.imageUrl || `/${cert.image}`}
                    alt={cert.name}
                    className="certificate-thumbnail"
                  />
                </div>
                <div className="certificate-details">
                  <h3>{cert.name}</h3>
                  <p><strong>카테고리:</strong> {cert.category}</p>
                  <p><strong>이미지:</strong> {cert.image}</p>
                  <p><strong>PDF:</strong> {cert.pdf}</p>
                  {cert.imageUrl && <p><strong>이미지 URL:</strong> {cert.imageUrl}</p>}
                  {cert.pdfUrl && <p><strong>PDF URL:</strong> {cert.pdfUrl}</p>}
                  <p><strong>순서:</strong> {cert.order}</p>
                </div>
                <div className="certificate-actions">
                  <button onClick={() => handleEditClick(cert)} className="edit-button">
                    수정
                  </button>
                  <button onClick={() => handleDelete(cert.id)} className="delete-button">
                    삭제
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificationManagement;
